/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Institutional Claim Request Form Tables
 */
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Caution, Delete, Plus } from '@icon-park/react';
import { Field, useForm, useFormEffects, useField } from '@formily/react';
import { FormItem } from '@formily/antd-v5';
import { Radio, Input, Button as AntdButton, Table, Select, Tooltip, Flex, InputProps } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { onFieldInputValueChange } from '@formily/core';

import Button from 'components/widgets/button';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { DatePicker } from 'lib/antd';
import { formatCurrency } from 'utils/admin/payments';
import { getDisplayOrCode, getQuantity } from 'utils/claims/claimRequest';
import { formatAmount } from 'utils/accounts/allAccounts';
import { CLAIM_TYPES, MODIFIER_CODES_KEYS } from 'app-constants/claims/claimRequest';
import { TitleWidget, RequiredTitle } from 'components/codeExplorer/CodeWidgets';
import { CODE_SET_MAPPING } from 'app-constants/codeExplorer';
import useClaimFilingStore from 'store/claimFiling';

const getRowIndex = (index: number): string => {
	return String.fromCharCode(65 + index) + '.';
};

type IRevenueCodeTableItem = InstitutionalClaimParser.IItemsInfo & { key: string };
type IAdditionalLineItemTableItem = InstitutionalClaimParser.IAdditionalLineItemTable & { key: string };

const CodeInput: React.FC<{ props: InputProps }> = (props) => {
	const field = useField<AnyType>();
	const invalidCodes = useClaimFilingStore((state) => state.invalidHcpcsCodes);
	const isInvalid = field.value && _.includes(invalidCodes, field.value);

	return (
		<Flex gap={4}>
			<Input
				{...props}
				value={field.value}
				onChange={(e) => field.onInput(e.target.value)}
				onBlur={field.onBlur}
				onFocus={field.onFocus}
				status={isInvalid ? 'warning' : null}
			/>
			{isInvalid ? (
				<Tooltip title={'This code is invalid.'}>
					<Caution theme='filled' size='16' fill='#D48806' />
				</Tooltip>
			) : null}
		</Flex>
	);
};

// Revenue code table
const RevenueCodeTable: React.FC<{
	value: InstitutionalClaimParser.IResponse;
	codeDetails?: CodeExplorer.ICodeSummaryResponse;
	isEditable?: boolean;
	modifierOptions?: IMultiSelectOption[];
	onChange?: (value: InstitutionalClaimParser.IResponse) => void;
}> = ({ value, codeDetails, isEditable = false, modifierOptions, onChange }) => {
	const items = value?.itemsInfo || [];
	const form = useForm();

	const [totalChargeSum, setTotalChargeSum] = useState(() => _.sumBy(items, (item) => _.toNumber(item?.netAmount?.value || 0)));

	useEffect(() => {
		setTotalChargeSum(_.sumBy(items, (item) => _.toNumber(item?.netAmount?.value || 0)));
	}, [items]);

	useFormEffects(() => {
		onFieldInputValueChange('row9.revenueCodeTable.table.itemsInfo.*.netAmount.value', () => {
			const formValues = form.values;
			const itemsInfo = formValues?.row9?.revenueCodeTable?.table?.itemsInfo || [];
			const newTotal = _.sumBy(itemsInfo, (item: AnyType) => _.toNumber(item?.netAmount?.value || 0));
			setTotalChargeSum(newTotal);
		});
	});

	const dataSource: IRevenueCodeTableItem[] = _.map(items, (item, index) => ({
		...item,
		key: index.toString(),
	}));

	const updateFieldValue = (updater: (currentValue: InstitutionalClaimParser.IItemsInfo[]) => InstitutionalClaimParser.IItemsInfo[]) => {
		const newValue = updater(_.cloneDeep(items));
		if (onChange) {
			const updatedValue = { ...value, itemsInfo: newValue };
			onChange(updatedValue);
		}
	};

	const handleDescriptionChange = (index: number, newDescription: string) => {
		updateFieldValue((currentValue) => {
			const updatedArray = [...currentValue];
			updatedArray[index].description = newDescription;
			return updatedArray;
		});
	};

	const handleTraceNumberChange = (index: number, newTraceNumber: string) => {
		updateFieldValue((currentValue) => {
			const updatedArray = [...currentValue];
			updatedArray[index].traceNumber = newTraceNumber;
			return updatedArray;
		});
	};

	const handleNonCoveredAmountChange = (index: number, newValue: number) => {
		updateFieldValue((currentValue) => {
			const updatedArray = [...currentValue];
			if (!updatedArray[index].nonCoveredAmount) {
				updatedArray[index].nonCoveredAmount = { value: 0, currency: 'USD' };
			}
			updatedArray[index].nonCoveredAmount.value = newValue;
			return updatedArray;
		});
	};

	const handleNDCChange = (index: number, field: 'code' | 'charge' | 'units' | 'measure', newValue: string | number) => {
		updateFieldValue((currentValue) => {
			const updatedArray = [...currentValue];
			if (!updatedArray[index].ndc) {
				updatedArray[index].ndc = { code: '', charge: { value: 0, currency: 'USD' }, units: 0, measure: '' };
			}

			if (field === 'charge') {
				updatedArray[index].ndc.charge = { value: newValue as number, currency: 'USD' };
			} else {
				(updatedArray[index].ndc as AnyType)[field] = newValue;
			}
			return updatedArray;
		});
	};

	// Add new row
	const addRow = () => {
		const newRow: InstitutionalClaimParser.IItemsInfo = {
			id: _.uniqueId('newItem_'),
			revenueCode: { code: null, system: fhirSystemMapping.revenueCode },
			description: null,
			servicePeriod: { start: '', end: '' },
			traceNumber: '',
			quantity: { value: null, code: 'UN' },
			netAmount: { value: null, currency: 'USD' },
			HCPCSCode: { code: null, system: fhirSystemMapping.hcpcsCode },
			modifiers: _.map(MODIFIER_CODES_KEYS, (key) => ({ key, code: null, system: fhirSystemMapping.hcpcsCode })),
			ndc: { code: null, charge: { value: 0, currency: 'USD' }, units: 0, measure: '' },
		};

		updateFieldValue((currentValue) => [...currentValue, newRow]);
	};

	const deleteRow = (index: number) => {
		const fieldPattern = `row9.revenueCodeTable.table.itemsInfo.${index}.*`;

		const fieldsToDestroy = form.query(fieldPattern);
		fieldsToDestroy.forEach((field) => {
			if (field && _.isFunction(field.destroy)) {
				field.destroy(true);
			}
		});

		// update the array (this will shift indices)
		updateFieldValue((currentValue) => _.filter(currentValue, (__, i) => i !== index));

		// destroy orphaned fields from the end of the array
		// After deletion, if we had 10 items and deleted index 8, we now have 9 items (indices 0-8)
		// But Formily still has fields for index 9, so we need to destroy those
		const newLength = items.length - 1;
		const originalLength = items.length;

		// Destroy fields for indices that no longer exist
		for (let i = newLength; i < originalLength; i++) {
			const orphanedFieldPattern = `row9.revenueCodeTable.table.itemsInfo.${i}.*`;
			form.query(orphanedFieldPattern).forEach((field) => {
				if (field && _.isFunction(field.destroy)) {
					field.destroy(true);
				}
			});
		}
	};

	const columns: ColumnsType<IRevenueCodeTableItem> = [
		{
			title: '',
			width: 30,
			render: (__: AnyType, _record: AnyType, index: number) => `${index + 1}.`,
		},
		{
			title: '42.',
			children: [
				{
					title: <RequiredTitle title='Revenue Code' required={isEditable} />,
					dataIndex: 'revenueCode',
					width: 100,
					key: 'revenueCode',
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.revenueCode.code`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[
										Input,
										{
											placeholder: 'Enter revenue code',
										},
									]}
								/>
							);
						}
						const codeDetailsItem = _.find(codeDetails?.result, { code: record.revenueCode?.code });
						if (codeDetailsItem) {
							return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
						}
						return record.revenueCode?.code || '';
					},
				},
			],
		},
		{
			title: '43.',
			children: [
				{
					title: 'Description',
					dataIndex: 'description',
					width: 200,
					key: 'description',
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.description || ''}
									onChange={(e) => handleDescriptionChange(index, e.target.value)}
									placeholder='Enter description'
								/>
							);
						}
						return record.description || '';
					},
				},
			],
		},
		{
			title: '44.',
			children: [
				{
					title: 'Line No.',
					dataIndex: 'traceNumber',
					width: 200,
					key: 'traceNumber',
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.traceNumber || ''}
									onChange={(e) => handleTraceNumberChange(index, e.target.value)}
									placeholder='Enter line number'
								/>
							);
						}
						return record.traceNumber || '';
					},
				},
			],
		},
		{
			title: '44 A.',
			children: [
				{
					title: (
						<TitleWidget
							title='HCPCS/HIPPS'
							codeSetSystem={[CODE_SET_MAPPING.HCPCS, CODE_SET_MAPPING.CPT, CODE_SET_MAPPING.HIPPS]}
							showSearchButton={isEditable}
							showRequiredIndicator={isEditable}
						/>
					),
					dataIndex: 'HCPCSCode',
					key: 'HCPCSCode',
					width: 100,
					render: (HCPCSCode: InstitutionalClaimParser.IItemsInfo['HCPCSCode'], _record: AnyType, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.HCPCSCode.code`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[CodeInput, { placeholder: 'Enter HCPCS code' }]}
								/>
							);
						}
						const codeDetailsItem = _.find(codeDetails?.result, { code: HCPCSCode?.code });
						if (codeDetailsItem) {
							return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
						}
						return HCPCSCode?.code || '';
					},
				},
			],
		},
		{
			title: '44 B. MODIFIERS',
			children: [
				{
					title: 'A',
					dataIndex: 'modifiers',
					width: 100,
					key: 'modifierA',
					render: (modifiers: InstitutionalClaimParser.IItemsInfo['modifiers'], _record: AnyType, index: number) => {
						const modifierA = _.find(modifiers, { key: 'A' });
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.modifiers.0.code`}
									decorator={[FormItem]}
									component={[
										Select,
										{ options: modifierOptions, showSearch: true, placeholder: 'Select', allowClear: true },
									]}
								/>
							);
						}
						const codeDetailsItem = _.find(codeDetails?.result, { code: modifierA?.code });
						if (codeDetailsItem) {
							return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
						}
						return modifierA?.code || '';
					},
				},
				{
					title: 'B',
					dataIndex: 'modifiers',
					width: 100,
					key: 'modifierB',
					render: (modifiers: InstitutionalClaimParser.IItemsInfo['modifiers'], _record: AnyType, index: number) => {
						const modifierB = _.find(modifiers, { key: 'B' });
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.modifiers.1.code`}
									decorator={[FormItem]}
									component={[
										Select,
										{ options: modifierOptions, showSearch: true, placeholder: 'Select', allowClear: true },
									]}
								/>
							);
						}
						const codeDetailsItem = _.find(codeDetails?.result, { code: modifierB?.code });
						if (codeDetailsItem) {
							return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
						}
						return modifierB?.code || '';
					},
				},
				{
					title: 'C',
					dataIndex: 'modifiers',
					width: 100,
					key: 'modifierC',
					render: (modifiers: InstitutionalClaimParser.IItemsInfo['modifiers'], _record: AnyType, index: number) => {
						const modifierC = _.find(modifiers, { key: 'C' });
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.modifiers.2.code`}
									decorator={[FormItem]}
									component={[
										Select,
										{ options: modifierOptions, showSearch: true, placeholder: 'Select', allowClear: true },
									]}
								/>
							);
						}
						const codeDetailsItem = _.find(codeDetails?.result, { code: modifierC?.code });
						if (codeDetailsItem) {
							return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
						}
						return modifierC?.code || '';
					},
				},
				{
					title: 'D',
					dataIndex: 'modifiers',
					width: 100,
					key: 'modifierD',
					render: (modifiers: InstitutionalClaimParser.IItemsInfo['modifiers'], _record: AnyType, index: number) => {
						const modifierD = _.find(modifiers, { key: 'D' });
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.modifiers.3.code`}
									decorator={[FormItem]}
									component={[
										Select,
										{ options: modifierOptions, showSearch: true, placeholder: 'Select', allowClear: true },
									]}
								/>
							);
						}
						const codeDetailsItem = _.find(codeDetails?.result, { code: modifierD?.code });
						if (codeDetailsItem) {
							return <Button.ShowClaimCode codeDetails={codeDetailsItem} />;
						}
						return modifierD?.code || '';
					},
				},
			],
		},
		{
			title: '45 Service Dates',
			key: 'servicePeriod',
			children: [
				{
					title: <RequiredTitle title='Start' required={isEditable} />,
					dataIndex: 'servicePeriod',
					width: 150,
					key: 'start',
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.servicePeriod.start`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[DatePicker, { placeholder: 'MM/DD/YYYY' }]}
								/>
							);
						}
						return record.servicePeriod?.start || '';
					},
				},
				{
					title: <RequiredTitle title='End' required={isEditable} />,
					dataIndex: 'servicePeriod',
					width: 150,
					key: 'end',
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.servicePeriod.end`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[DatePicker, { placeholder: 'MM/DD/YYYY' }]}
								/>
							);
						}
						return record.servicePeriod?.end || '';
					},
				},
			],
		},
		{
			title: '46.',
			children: [
				{
					title: <RequiredTitle title='Service Units' required={isEditable} />,
					dataIndex: 'quantity',
					key: 'quantity',
					width: 100,
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.quantity.value`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[Input, { placeholder: '0.00', type: 'number' }]}
								/>
							);
						}
						return getQuantity(record.quantity);
					},
				},
			],
		},
		{
			title: '47.',
			children: [
				{
					title: <RequiredTitle title='Total Charge' required={isEditable} />,
					dataIndex: 'netAmount',
					key: 'netAmount',
					width: 100,
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Field
									name={`itemsInfo.${index}.netAmount.value`}
									validator={[
										{
											required: true,
										},
									]}
									decorator={[FormItem, { feedbackLayout: 'none' }]}
									component={[
										Input,
										{
											placeholder: '0.00',
											type: 'number',
											step: '0.01',
										},
									]}
								/>
							);
						}
						return formatCurrency(record.netAmount?.value);
					},
				},
			],
		},
		{
			title: '48.',
			children: [
				{
					title: 'Non-Covered Charge',
					dataIndex: 'nonCoveredAmount',
					key: 'nonCoveredAmount',
					width: 100,
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.nonCoveredAmount?.value || ''}
									onChange={(e) => handleNonCoveredAmountChange(index, Number(e.target.value) || 0)}
									placeholder='0.00'
									type='number'
									step='0.01'
								/>
							);
						}
						return formatCurrency(record.nonCoveredAmount?.value);
					},
				},
			],
		},
		{
			title: '49 NDC',
			key: 'ndc',
			children: [
				{
					title: 'Code',
					dataIndex: 'ndc',
					key: 'ndcCode',
					width: 100,
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.ndc?.code || ''}
									onChange={(e) => handleNDCChange(index, 'code', e.target.value)}
									placeholder='Enter NDC code'
								/>
							);
						}
						return record.ndc?.code || '';
					},
				},
				{
					title: 'Charge',
					dataIndex: 'ndc',
					key: 'ndcCharge',
					width: 100,
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.ndc?.charge?.value || ''}
									onChange={(e) => handleNDCChange(index, 'charge', Number(e.target.value) || 0)}
									placeholder='0.00'
									type='number'
									step='0.01'
								/>
							);
						}
						return formatCurrency(record.ndc?.charge?.value);
					},
				},
				{
					title: 'Units',
					dataIndex: 'ndc',
					key: 'ndcUnits',
					width: 100,
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.ndc?.units || ''}
									onChange={(e) => handleNDCChange(index, 'units', e.target.value)}
									placeholder='Enter units'
									type='number'
								/>
							);
						}
						return record.ndc?.units || '';
					},
				},
				{
					title: 'Measure',
					dataIndex: 'ndc',
					key: 'ndcMeasure',
					width: 100,
					render: (__: AnyType, record: IRevenueCodeTableItem, index: number) => {
						if (isEditable) {
							return (
								<Input
									value={record.ndc?.measure || ''}
									onChange={(e) => handleNDCChange(index, 'measure', e.target.value)}
									placeholder='Enter measure'
								/>
							);
						}
						return record.ndc?.measure || '';
					},
				},
			],
		},
		// Add delete button column when editable
		...(isEditable
			? [
					{
						title: '',
						key: 'action',
						width: 50,
						render: (__: AnyType, _record: IRevenueCodeTableItem, index: number) => (
							<AntdButton
								type='text'
								disabled={_.size(items) <= 1}
								danger
								icon={<Delete />}
								onClick={() => deleteRow(index)}
								size='small'
							/>
						),
					},
				]
			: []),
	];

	return (
		<div id='revenue-code-table-container' style={{ maxHeight: '400px', overflowY: 'auto' }}>
			<Table
				className='claim-form-table'
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				bordered
				size='small'
				rowKey='key'
				scroll={{ x: 'max-content' }}
				summary={() => {
					const totalNonCoveredCharge = _.sumBy(items, (item) => _.toNumber(item?.nonCoveredAmount?.value || 0));
					return (
						<Table.Summary.Row>
							<Table.Summary.Cell index={0} colSpan={12}>
								<b>{'Total'}</b>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={1} align='right'>
								<b>{formatAmount(totalChargeSum)}</b>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={2} align='right'>
								<b>{formatAmount(totalNonCoveredCharge)}</b>
							</Table.Summary.Cell>
							<Table.Summary.Cell index={2} colSpan={6} />
						</Table.Summary.Row>
					);
				}}
			/>
			{isEditable && (
				<div className='mt-4 mb-2 text-center'>
					<AntdButton disabled={_.size(items) >= 999} type='dashed' icon={<Plus />} onClick={addRow} size='large'>
						Add Line Item
					</AntdButton>
				</div>
			)}
		</div>
	);
};

// Payer table
const PayerTable: React.FC<{ value: InstitutionalClaimParser.IResponse }> = ({ value }) => {
	const dataSource = _.map(_.range(3), (index) => {
		const payer = value?.subscribersAndPayers?.[index]?.payer;
		return {
			id: index,
			name: payer?.name ?? '',
			payerId: payer?.payorIdentification?.value ?? '',
			healthPlanId: payer?.payorIdentification?.value ?? '',
			releaseOfInformationCode: payer ? value?.extensionMeta?.clmSection?.releaseOfInformationCode?.code : '',
			planAssignmentCode: payer ? value?.extensionMeta?.clmSection?.planAssignmentCode?.code : '',
			benefitAssignmentIndicator: payer ? getDisplayOrCode(value?.extensionMeta?.clmSection?.benefitAssignmentIndicator) : '',
			// todo recheck( it doesn't come in 5010, but in 4010)
			priorPayments: '',
			estAmountDue: '',
			billingProviderSecondaryId: payer ? value?.billingProvider?.npi?.value : '',
		};
	});

	// Define table columns
	const columns = [
		{
			title: '',
			width: 30,
			render: (__: AnyType, _record: AnyType, index: number) => getRowIndex(index),
		},
		{
			title: '50.',
			children: [
				{
					title: 'Payer',
					dataIndex: 'name',
					key: 'name',
				},
				{
					title: 'Payer ID',
					dataIndex: 'payerId',
					key: 'payerId',
				},
			],
		},
		{
			title: '51.',
			children: [
				{
					title: 'Health Plan ID',
					dataIndex: 'healthPlanId',
					key: 'healthPlanId',
				},
			],
		},
		{
			title: '52.',
			children: [
				{
					title: 'Rel Info',
					dataIndex: 'releaseOfInformationCode',
					key: 'releaseOfInformationCode',
				},
			],
		},
		{
			title: '53.',
			children: [
				{
					title: 'ASG',
					dataIndex: 'planAssignmentCode',
					key: 'planAssignmentCode',
				},
				{
					title: 'Value',
					dataIndex: 'benefitAssignmentIndicator',
					key: 'benefitAssignmentIndicator',
				},
			],
		},
		{
			title: '54.',
			children: [
				{
					title: 'Prior Payments',
					dataIndex: 'priorPayments',
					key: 'priorPayments',
				},
			],
		},
		{
			title: '55.',
			children: [
				{
					title: 'Est. Amount Due',
					dataIndex: 'estAmountDue',
					key: 'estAmountDue',
				},
			],
		},
		{
			title: '57.',
			children: [
				{
					title: 'Billing Provider Secondary ID',
					dataIndex: 'billingProviderSecondaryId',
					key: 'billingProviderSecondaryId',
				},
			],
		},
	];

	return (
		<Table
			className='claim-form-table'
			columns={columns}
			dataSource={dataSource}
			pagination={false}
			size='small'
			bordered
			rowKey='id'
		/>
	);
};

// Insured table
const InsuredTable: React.FC<{ value: InstitutionalClaimParser.ISubscriberAndPayer[] }> = ({ value }) => {
	// get gender
	const getGender = (gender: string): React.ReactNode => {
		if (!gender) {
			return null;
		}
		const genderValue = _.includes(['male', 'female', 'unknown'], gender) ? gender : '';
		const options: Record<string, string> = { male: 'M', female: 'F', unknown: 'Unknown' };

		return (
			<Radio.Group value={genderValue}>
				{_.map(options, (label, key) => (
					<Radio key={key} value={key}>
						{label}
					</Radio>
				))}
			</Radio.Group>
		);
	};

	// data source for the table with 3 rows (A, B, C)
	const dataSource = _.map(_.range(3), (index) => {
		const subscriber = value?.[index]?.subscriber;
		const coverage = value?.[index]?.coverage;
		return {
			id: index,
			firstName: subscriber?.nameMeta?.[0]?.firstName ?? '',
			middleName: subscriber?.nameMeta?.[0]?.middleNames?.[0] ?? '',
			lastName: subscriber?.nameMeta?.[0]?.lastName ?? '',
			dateOfBirth: subscriber?.birthDate ?? '',
			gender: subscriber?.gender ?? '',
			patientRelation: subscriber?.patientRelation ?? '',
			uniqueId: subscriber?.memberId?.value ?? subscriber?.agencyClaimNumber?.value ?? '',
			groupName: coverage?.planName ?? '',
			insuranceGroupNo: coverage?.planCode ?? '',
		};
	});

	// Define table columns
	const columns = [
		{
			title: '',
			width: 30,
			render: (__: AnyType, _record: AnyType, index: number) => getRowIndex(index),
		},
		{
			title: "58 INSURED'S NAME",
			children: [
				{
					title: 'First',
					dataIndex: 'firstName',
					key: 'firstName',
				},
				{
					title: 'Middle',
					dataIndex: 'middleName',
					key: 'middleName',
				},
				{
					title: 'Last',
					dataIndex: 'lastName',
					key: 'lastName',
				},
				{
					title: 'Date of Birth',
					dataIndex: 'dateOfBirth',
					key: 'dateOfBirth',
				},
				{
					title: 'Gender',
					dataIndex: 'gender',
					key: 'gender',
					render: (gender: string) => getGender(gender),
				},
			],
		},
		{
			title: '59.',
			children: [
				{
					title: 'Patient Relation',
					dataIndex: 'patientRelation',
					key: 'patientRelation',
				},
			],
		},
		{
			title: '60.',
			children: [
				{
					title: "Insured's Unique ID",
					dataIndex: 'uniqueId',
					key: 'uniqueId',
				},
			],
		},
		{
			title: '61.',
			children: [
				{
					title: 'Group Name',
					dataIndex: 'groupName',
					key: 'groupName',
				},
			],
		},
		{
			title: '62.',
			children: [
				{
					title: 'Insurance Group No.',
					dataIndex: 'insuranceGroupNo',
					key: 'insuranceGroupNo',
				},
			],
		},
	];

	return (
		<Table
			className='claim-form-table'
			columns={columns}
			dataSource={dataSource}
			pagination={false}
			size='small'
			bordered
			rowKey='id'
		/>
	);
};

// Treatment authorization table
const TreatmentAuthorizationTable: React.FC<{ value: InstitutionalClaimParser.IResponse }> = ({ value }) => {
	//  Default row data with 3 rows (A, B, C)( We can only have max 1 row for treatment authorization)
	const dataSource = _.map(_.range(3), (index) => {
		const subscriberName = value?.subscribersAndPayers?.[index]?.subscriber?.name;
		if (index === 0) {
			return {
				id: index,
				treatmentAuthCode: value?.claimInfo?.referralNumber?.value || value?.claimInfo?.priorAuthorizationNumber?.value,
				documentControlNumber: value?.claimInfo?.claimNumber?.value,
				employerName: subscriberName,
			};
		}
		return {
			id: index,
			treatmentAuthCode: '',
			documentControlNumber: '',
			employerName: '',
		};
	});

	// Define table columns
	const columns = [
		{
			title: '',
			width: 30,
			render: (__: AnyType, _record: AnyType, index: number) => getRowIndex(index),
		},
		{
			title: '63.',
			children: [
				{
					title: 'Treatment Authorization Codes',
					dataIndex: 'treatmentAuthCode',
					key: 'treatmentAuthCode',
				},
			],
		},
		{
			title: '64.',
			children: [
				{
					title: 'Document Control Number',
					dataIndex: 'documentControlNumber',
					key: 'documentControlNumber',
				},
			],
		},
		{
			title: '65.',
			children: [
				{
					title: 'Employer Name',
					dataIndex: 'employerName',
					key: 'employerName',
				},
			],
		},
	];

	return (
		<Table
			className='claim-form-table'
			columns={columns}
			dataSource={dataSource}
			pagination={false}
			size='small'
			bordered
			rowKey='id'
		/>
	);
};

type ITableData = {
	key: string;
	id: number;
	qual: string;
	code: string;
	value: string;
};

// Qualification table
const QualTable: React.FC<{
	value: InstitutionalClaimParser.IResponse;
	isEditable?: boolean;
	onChange?: (value: InstitutionalClaimParser.IResponse) => void;
}> = ({ value, isEditable = false, onChange }) => {
	// ICD system mapping for qual
	const getIcdSystemMappingForQual = {
		'http://terminology.hl7.org/CodeSystem/icd-10-cm': 'ABN',
		'http://terminology.hl7.org/CodeSystem/icd-9-cm': 'BN',
	};

	// Reverse mapping for editing
	const reverseIcdSystemMapping = {
		ABN: 'http://terminology.hl7.org/CodeSystem/icd-10-cm',
		BN: 'http://terminology.hl7.org/CodeSystem/icd-9-cm',
	};

	// Qual options for select dropdown
	const qualOptions = [
		{ key: 'ABN', label: 'ABN', value: 'ABN' },
		{ key: 'BN', label: 'BN', value: 'BN' },
	];

	// Create data source for the table with 4 rows
	const dataSource: ITableData[] = useMemo(() => {
		const otherInjuryCode = _.slice(value?.supportingInfoMeta?.externalCauseOfInjury, 3, 7);
		return _.map(_.range(4), (index) => {
			const coding = otherInjuryCode?.[index];
			if (!coding) {
				return {
					key: index.toString(),
					id: index,
					qual: '',
					code: '',
					value: '',
				};
			}
			return {
				key: index.toString(),
				id: index,
				qual: getIcdSystemMappingForQual?.[coding?.systemMeta?.system] ?? '',
				code: coding?.code ?? '',
				value: coding?.systemMeta?.display ?? '',
			};
		});
	}, [value]);

	// Handle field updates for editable fields
	const handleFieldUpdate = (index: number, field: string, newValue: string) => {
		if (onChange) {
			const updatedValue = _.cloneDeep(value);

			// Ensure supportingInfoMeta exists
			if (!updatedValue.supportingInfoMeta) {
				updatedValue.supportingInfoMeta = {} as InstitutionalClaimParser.ISupportingInfoMeta;
			}
			if (!updatedValue.supportingInfoMeta.externalCauseOfInjury) {
				updatedValue.supportingInfoMeta.externalCauseOfInjury = [];
			}

			// Get the current external cause of injury array
			const externalCauseArray = [...updatedValue.supportingInfoMeta.externalCauseOfInjury];

			// Calculate the actual index in the array (index + 3 because we slice from 3)
			const actualIndex = index + 3;

			// Ensure we have enough elements in the array
			while (externalCauseArray.length <= actualIndex) {
				externalCauseArray.push({
					code: '',
					system: '',
					systemMeta: {
						id: null,
						extension: null,
						system: '',
						version: null,
						code: '',
						display: '',
						userSelected: null,
					},
					indicator: undefined,
				});
			}

			// Ensure the item at actualIndex exists and has proper structure
			if (!externalCauseArray[actualIndex]) {
				externalCauseArray[actualIndex] = {
					code: '',
					system: '',
					systemMeta: {
						id: null,
						extension: null,
						system: '',
						version: null,
						code: '',
						display: '',
						userSelected: null,
					},
					indicator: undefined,
				};
			}

			// Ensure systemMeta exists
			if (!externalCauseArray[actualIndex].systemMeta) {
				externalCauseArray[actualIndex].systemMeta = {
					id: null,
					extension: null,
					system: '',
					version: null,
					code: '',
					display: '',
					userSelected: null,
				};
			}

			// Handle different field updates
			switch (field) {
				case 'qual': {
					const systemUrl = reverseIcdSystemMapping[newValue] || '';
					externalCauseArray[actualIndex].system = systemUrl;
					externalCauseArray[actualIndex].systemMeta.system = systemUrl;
					break;
				}
				case 'code': {
					externalCauseArray[actualIndex].code = newValue;
					externalCauseArray[actualIndex].systemMeta.code = newValue;
					break;
				}
				case 'value': {
					externalCauseArray[actualIndex].systemMeta.display = newValue;
					break;
				}
			}

			// Update the external cause array
			updatedValue.supportingInfoMeta.externalCauseOfInjury = externalCauseArray;
			onChange(updatedValue);
		}
	};

	// Define table columns
	const columns = [
		{
			title: '',
			width: 30,
			render: (__: AnyType, _record: AnyType, index: number) => getRowIndex(index),
		},
		{
			title: '81.',
			children: [
				{
					title: 'QUAL.',
					dataIndex: 'qual',
					key: 'qual',
					render: (qual: string, record: ITableData) => {
						if (isEditable) {
							return (
								<Select
									value={qual || undefined}
									onChange={(selectedValue) => handleFieldUpdate(record.id, 'qual', selectedValue)}
									placeholder='Select qual'
									style={{ width: '100%' }}
									options={qualOptions}
									allowClear
								/>
							);
						}
						return qual || '';
					},
				},
				{
					title: 'Code',
					dataIndex: 'code',
					key: 'code',
					render: (code: string, record: ITableData) => {
						if (isEditable) {
							return (
								<Input
									value={code || ''}
									onChange={(e) => handleFieldUpdate(record.id, 'code', e.target.value)}
									placeholder='Enter code'
								/>
							);
						}
						return code || '';
					},
				},
				{
					title: 'Value',
					dataIndex: 'value',
					key: 'value',
					render: (displayValue: string, record: ITableData) => {
						if (isEditable) {
							return (
								<Input
									value={displayValue || ''}
									onChange={(e) => handleFieldUpdate(record.id, 'value', e.target.value)}
									placeholder='Enter value'
								/>
							);
						}
						return displayValue || '';
					},
				},
			],
		},
	];

	return (
		<Table
			className='claim-form-table'
			columns={columns}
			dataSource={dataSource}
			pagination={false}
			size='small'
			bordered
			rowKey='id'
		/>
	);
};

const formatAdjustmentCodesAsText = (adjustments: InstitutionalClaimParser.IAdjustment[], showAmount = false): string => {
	if (_.isEmpty(adjustments)) {
		return '';
	}
	return _.compact(
		_.map(adjustments, (adjustment) => {
			const groupCode = adjustment.groupCode || '';
			const reasonCode = adjustment.reasonCode || '';
			const code = groupCode && reasonCode ? `${groupCode}-${reasonCode}` : groupCode || reasonCode;
			const amountSuffix = showAmount ? ` ($${adjustment.amount})` : '';
			return code ? `${code}${amountSuffix}` : '';
		}),
	).join(', ');
};

const getModifierDisplay = (modifier: InstitutionalClaimParser.IAdditionalLineItemTable['modifier']): string => {
	if (_.isEmpty(modifier)) {
		return '';
	}
	return _.compact(_.map(modifier, (mod) => mod?.code)).join(', ');
};

const AdditionalLineItemTable: React.FC<{
	value: InstitutionalClaimParser.IResponse;
}> = ({ value }) => {
	const items = value?.additionalLineItemTable || [];
	const isInstitutionalClaim = value?.claimInfo?.type === CLAIM_TYPES.institutional.value;

	const dataSource: IAdditionalLineItemTableItem[] = _.map(items, (item, index) => ({
		...item,
		key: index.toString(),
	}));

	const columns: ColumnsType<IAdditionalLineItemTableItem> = [
		...(isInstitutionalClaim
			? [
					{
						title: 'Rev Code',
						dataIndex: 'revenueCode',
						key: 'revenueCode',
						render: (revenueCode: { code?: string }) => revenueCode?.code ?? '',
					},
				]
			: []),
		{
			title: 'Procedure Code',
			dataIndex: 'HCPCSCode',
			key: 'HCPCSCode',
			render: (hcpcsCode: { code?: string }) => hcpcsCode?.code ?? '',
		},
		{
			title: 'Modifiers',
			dataIndex: 'modifier',
			key: 'modifier',
			render: (modifiers: InstitutionalClaimParser.IAdditionalLineItemTable['modifier']) => getModifierDisplay(modifiers),
		},
		{ title: 'Units', dataIndex: 'quantity', key: 'quantity', render: (quantity: number) => quantity ?? '' },
		{
			title: 'Billed',
			dataIndex: 'billedAmount',
			key: 'billedAmount',
			render: (billedAmount: number) => formatCurrency(billedAmount),
		},
		{
			title: 'Allowed',
			dataIndex: 'allowedAmount',
			key: 'allowedAmount',
			render: (allowedAmount: number) => formatCurrency(allowedAmount),
		},
		{
			title: 'PR',
			dataIndex: 'patientResponsibilityAmount',
			key: 'patientResponsibilityAmount',
			render: (patientResponsibilityAmount: number) => formatCurrency(patientResponsibilityAmount),
		},
		{
			title: 'Adjusted',
			dataIndex: 'adjustedAmount',
			key: 'adjustedAmount',
			render: (adjustedAmount: number) => formatCurrency(adjustedAmount),
		},
		{ title: 'Paid', dataIndex: 'paidAmount', key: 'paidAmount', render: (paidAmount: number) => formatCurrency(paidAmount) },
		{
			title: 'Adjustment Codes',
			dataIndex: 'adjustments',
			key: 'adjustmentCodes',
			render: (adjustments: InstitutionalClaimParser.IAdjustment[]) => formatAdjustmentCodesAsText(adjustments, true),
		},
	];

	return (
		<div className='max-h-[400px] overflow-y-auto'>
			<Table
				className='claim-form-table'
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				bordered
				size='small'
				rowKey='key'
				scroll={{ x: 'max-content' }}
			/>
		</div>
	);
};

const ClaimAdjudicationTable: React.FC<{
	value: InstitutionalClaimParser.IResponse;
}> = ({ value }) => {
	const item = value?.claimAdjudicationInfo;
	const dataSource = item ? [{ ...item, key: '0' }] : [];

	const columns = [
		{ title: 'Billed', dataIndex: 'billedAmount', key: 'billedAmount', render: (billedAmount: number) => formatCurrency(billedAmount) },
		{
			title: 'Paid',
			dataIndex: 'primaryPayerPayment',
			key: 'primaryPayerPayment',
			render: (primaryPayerPayment: number) => formatCurrency(primaryPayerPayment),
		},
		{
			title: 'Adjusted',
			dataIndex: 'adjustedAmount',
			key: 'adjustedAmount',
			render: (adjustedAmount: number) => formatCurrency(adjustedAmount),
		},
		{
			title: 'PR',
			dataIndex: 'remainingPatientLiability',
			key: 'remainingPatientLiability',
			render: (remainingPatientLiability: number) => formatCurrency(remainingPatientLiability),
		},
		{
			title: 'Adjustment Codes',
			dataIndex: 'adjustments',
			key: 'adjustmentCodes',
			render: (adjustments: InstitutionalClaimParser.IAdjustment[]) => formatAdjustmentCodesAsText(adjustments),
		},
	];

	return (
		<div className='max-h-[400px] overflow-y-auto'>
			<Table
				className='claim-form-table'
				columns={columns}
				dataSource={dataSource}
				pagination={false}
				bordered
				size='small'
				rowKey='key'
				scroll={{ x: 'max-content' }}
			/>
		</div>
	);
};

export {
	CodeInput,
	QualTable,
	TreatmentAuthorizationTable,
	InsuredTable,
	PayerTable,
	RevenueCodeTable,
	AdditionalLineItemTable,
	ClaimAdjudicationTable,
};
